/**
 * Pure helpers for the video-note recorder: how the camera is opened (an injectable strategy, so the
 * dashboard and a future extension surface share one component), which container the browser can
 * record, and how fast the teleprompter scrolls.
 */

/** Longest recording, in ms. The server trims to the same cap when it transcodes. */
export const VIDEO_NOTE_MAX_MS = 60_000;
/** Seconds counted down on screen before recording starts, so the rep can look up from the script. */
export const VIDEO_NOTE_COUNTDOWN_SECONDS = 3;

/**
 * How the recorder gets a camera + mic stream. The dashboard uses `getUserMedia` directly; a surface
 * where that is unreliable (the Chrome extension side panel) can inject its own.
 */
export type VideoMediaStrategy = {
  getStream: () => Promise<MediaStream>;
};

/** Portrait, front camera, 720×1280 ideal — the shape the server transcodes to anyway. */
export const VIDEO_NOTE_CONSTRAINTS: MediaStreamConstraints = {
  audio: true,
  video: {
    facingMode: 'user',
    width: { ideal: 720 },
    height: { ideal: 1280 },
    aspectRatio: { ideal: 9 / 16 },
  },
};

export const browserVideoMediaStrategy: VideoMediaStrategy = {
  getStream: () => navigator.mediaDevices.getUserMedia(VIDEO_NOTE_CONSTRAINTS),
};

/** Preference order: MP4 where the browser can record it (Safari), else WebM. The server transcodes either way. */
export const VIDEO_NOTE_MIME_PREFERENCE = [
  'video/mp4;codecs=avc1,mp4a',
  'video/mp4',
  'video/webm;codecs=vp9,opus',
  'video/webm;codecs=vp8,opus',
  'video/webm',
] as const;

/** First container this browser can record, or undefined to let MediaRecorder pick its default. */
export function pickVideoNoteMimeType(isTypeSupported: (type: string) => boolean): string | undefined {
  return VIDEO_NOTE_MIME_PREFERENCE.find((type) => isTypeSupported(type));
}

export type TeleprompterSpeed = 'slow' | 'normal' | 'fast';

/** Spoken words per minute for each speed — a relaxed on-camera read sits around 140–150. */
export const TELEPROMPTER_WPM: Record<TeleprompterSpeed, number> = { slow: 115, normal: 145, fast: 175 };

/** How long the script takes to scroll past at a given speed (never shorter than 4 s, so it stays readable). */
export function teleprompterDurationMs(script: string, speed: TeleprompterSpeed): number {
  const words = script.trim().split(/\s+/).filter(Boolean).length;
  return Math.max(4_000, Math.round((words / TELEPROMPTER_WPM[speed]) * 60_000));
}

/** mm:ss for the on-screen timer. */
export function formatClock(ms: number): string {
  const total = Math.max(0, Math.floor(ms / 1000));
  return `${Math.floor(total / 60)}:${String(total % 60).padStart(2, '0')}`;
}

/** Why the camera could not open, mapped from the DOMException name getUserMedia rejects with. */
export function cameraErrorKind(error: unknown): 'denied' | 'no_camera' | 'other' {
  const name = error instanceof Error ? error.name : '';
  if (name === 'NotAllowedError' || name === 'SecurityError') return 'denied';
  if (name === 'NotFoundError' || name === 'OverconstrainedError' || name === 'NotReadableError') return 'no_camera';
  return 'other';
}
